import { LAYOUT, THUMBNAIL_SIZE_LIST } from "@lhd/constants";
import type { MenuProps } from "antd";
import { EditorState } from "draft-js";
import {
  AiOutlineBank,
  AiOutlineBranches,
  AiOutlineCalendar,
  AiOutlineGroup,
  AiOutlineLink,
  AiOutlinePhone,
  AiOutlineUser,
} from "react-icons/ai";
import {
  BiCheckboxChecked,
  BiRadioCircleMarked,
  BiSelectMultiple,
  BiTable,
} from "react-icons/bi";
import {
  BsFillDiagram2Fill,
  BsJournalRichtext,
  BsNewspaper,
  BsTextarea,
} from "react-icons/bs";
import { FaSignature, FaUserAlt } from "react-icons/fa";
import { ImAttachment } from "react-icons/im";
import { IoMdTime } from "react-icons/io";
import { MdArrowDropDown } from "react-icons/md";
import { RiFileSearchLine, RiOrganizationChart } from "react-icons/ri";
import { TbMinus, TbMinusVertical, TbNumbers } from "react-icons/tb";
import type { AppPartType } from "./types";
import { VscOrganization } from "react-icons/vsc";

export const KEY_MENU = {
  EDIT: "EDIT",
  DUPLICATE: "DUPLICATE",
  DELETE: "DELETE",
  ADD_TO_LEFT: "ADD_TO_LEFT",
  ADD_TO_RIGHT: "ADD_TO_RIGHT",
};

export const ITEMS_MENU: MenuProps["items"] = [
  {
    key: KEY_MENU.EDIT,
    label: "設定",
  },
  {
    key: KEY_MENU.DUPLICATE,
    label: "複製",
  },
  {
    key: KEY_MENU.DELETE,
    label: "削除",
  },
  {
    key: KEY_MENU.ADD_TO_LEFT,
    label: "",
  },
  {
    key: KEY_MENU.ADD_TO_RIGHT,
    label: "",
  },
];

export const ItemTypes = {
  BOX: "box",
};

export const MAX_COLUMN = 2880;
export const COLUMN_WIDTH = 6.25;
export const ROW_HEIGHT = 25;
export const MARGIN_X = COLUMN_WIDTH;
export const COMPACT_TYPE = null; // "horizontal" | "vertical" | null | undefined
export const DEFAULT_ROW_HEIGHT = 5;
export const DEFAULT_MIN_W = 9;

export const LEVEL_LINE = {
  level_horizontal_0: "level_horizontal_0",
  level_horizontal_1: "level_horizontal_1",
  level_horizontal_2: "level_horizontal_2",
  level_horizontal_3: "level_horizontal_3",
  level_horizontal_4: "level_horizontal_4",
  level_vertical_0: "level_vertical_0",
  level_vertical_1: "level_vertical_1",
  level_vertical_2: "level_vertical_2",
  level_vertical_3: "level_vertical_3",
  level_vertical_4: "level_vertical_4",
};

export const APP_PART_TYPES = {
  LABEL: "label",
  TEXT: "text",
  RICH_TEXT: "rich_text",
  TEXT_AREA: "text_area",
  NUMBER: "number",
  CALCULATED: "calculated",
  RADIO_BUTTON: "radio_button",
  CHECK_BOX: "check_box",
  DATE: "date",
  TIME: "time",
  DATE_AND_TIME: "date_and_time",
  ATTACHMENT: "attachment",
  LINK: "link",
  WORKER_SELECTION: "worker_selection",
  BRANCH_SELECTION: "branch_selection",
  DEPARTMENT_SELECTION: "department_selection",
  DIVISION_SELECTION: "division_selection",
  ORGANIZATION_SELECTION: "organization_selection",
  RELATED_RECORDS: "related_records",
  LOOKUP: "lookup",
  BLANK_SPACE: "blank_space",
  HORIZONTAL_BORDER: "border", // it is horizontal_border
  VERTICAL_BORDER: "vertical_border",
  FIELD_GROUP: "field_group",
  TABLE: "table",
  RECORD_NUMBER: "record_number",
  CREATED_BY: "created_by",
  CREATED_DATETIME: "created_datetime",
  UPDATED_BY: "updated_by",
  UPDATED_DATETIME: "updated_datetime",
  DROPDOWN: "dropdown",
  MULTI_CHOICE: "multichoice",
  SIGNATURE: "signature",
  STATUS_CONTRACT: "status_contract",
  SIGNED_DATE: "signed_date",
  PRIMARY_COMPANY_NAME: "primary_company_name",
  COMPANY_LOCATION_SELECTION: "company_location_selection",
  REPRESENTATIVE_SELECTION: "representative_selection",
  COMPANY_PHONE_NUMBER_SELECTION: "company_phone_number_selection",
  POSITION_SELECTION: "position_selection",
  EMPLOYEE_INFO_SELECTION: "employee_info_selection",
  WORKER_ADDRESSES_SELECTION: "worker_addresses_selection",
  WORKER_PHONE_NUMBERS_LANDLINE_SELECTION:
    "worker_phone_numbers_landline_selection",
  WORKER_PHONE_NUMBERS_MOBILE_SELECTION:
    "worker_phone_numbers_mobile_selection",
  USER_WORKER_EMAILS_SELECTION: "user_worker_emails_selection",
  WORKER_STORES_SELECTION: "worker_stores_selection",
  WORKER_REGISTRATION_NUMBERS_SELECTION:
    "worker_registration_numbers_selection",
  WORKER_EMERGENCY_CONTACTS_SELECTION: "worker_emergency_contacts_selection",
  WORKER_EMERGENCY_ADDRESSES_SELECTION: "worker_emergency_addresses_selection",
  WORKER_ONBOARDINGS_SELECTION: "worker_onboardings_selection",
  WORKER_INSURANCES_REGULAR_INSURANCE_SELECTION:
    "worker_insurances_regular_insurance_selection",
  WORKER_INSURANCES_UNEMPLOYMENT_INSURANCE_SELECTION:
    "worker_insurances_unemployment_insurance_selection",
  WORKER_COMPANY_EMAILS_SELECTION: "worker_company_emails_selection",
  WORKER_DEVICES_SELECTION: "worker_devices_selection",
  CUSTOMER_SELECTION: "customer_selection",
  EMPLOYEE_SIGNATURE: "employee_signature",
} as const;

export const JP_APP_PART_TYPES = {
  [APP_PART_TYPES.LABEL]: "ラベル 設定",
  [APP_PART_TYPES.TEXT]: "テキスト設定",
  [APP_PART_TYPES.RICH_TEXT]: "リッチエディター設定",
  [APP_PART_TYPES.TEXT_AREA]: "テキストエリア設定",
  [APP_PART_TYPES.NUMBER]: "数値設定",
  [APP_PART_TYPES.CALCULATED]: "計算設定",
  [APP_PART_TYPES.RADIO_BUTTON]: "ラジオボタン設定",
  [APP_PART_TYPES.CHECK_BOX]: "設定",
  [APP_PART_TYPES.DATE]: "日付設定",
  [APP_PART_TYPES.TIME]: "時刻設定",
  [APP_PART_TYPES.DATE_AND_TIME]: "日付・時刻設定",
  [APP_PART_TYPES.ATTACHMENT]: "添付ファイル設定",
  [APP_PART_TYPES.LINK]: "リンク設定",
  [APP_PART_TYPES.WORKER_SELECTION]: "社員選択設定",
  [APP_PART_TYPES.BRANCH_SELECTION]: "支店選択設定",
  [APP_PART_TYPES.DEPARTMENT_SELECTION]: "部門選択設定",
  [APP_PART_TYPES.DIVISION_SELECTION]: "部署選択設定",
  [APP_PART_TYPES.ORGANIZATION_SELECTION]: "組織選択設定",
  [APP_PART_TYPES.RELATED_RECORDS]: "Bizシート参照設定",
  [APP_PART_TYPES.LOOKUP]: "検索設定",
  [APP_PART_TYPES.BLANK_SPACE]: "空白スペース設定",
  [APP_PART_TYPES.FIELD_GROUP]: "フィルードグループ設定",
  [APP_PART_TYPES.TABLE]: "テーブル設定",
  [APP_PART_TYPES.RECORD_NUMBER]: "レコード数設定",
  [APP_PART_TYPES.CREATED_BY]: "作成者設定",
  [APP_PART_TYPES.CREATED_DATETIME]: "作成日時設定",
  [APP_PART_TYPES.UPDATED_BY]: "更新者設定",
  [APP_PART_TYPES.UPDATED_DATETIME]: "更新日時設定",
  [APP_PART_TYPES.DROPDOWN]: "ドロップダウン設定",
  [APP_PART_TYPES.MULTI_CHOICE]: "複数選択設定",
  [APP_PART_TYPES.SIGNED_DATE]: "署名日設定",
  [APP_PART_TYPES.PRIMARY_COMPANY_NAME]: "自社名設定",
  [APP_PART_TYPES.COMPANY_LOCATION_SELECTION]: "自社住所設定",
  [APP_PART_TYPES.REPRESENTATIVE_SELECTION]: "代表者名設定",
  [APP_PART_TYPES.COMPANY_PHONE_NUMBER_SELECTION]: "会社電話番号設定",
  [APP_PART_TYPES.POSITION_SELECTION]: "役職設定",
  [APP_PART_TYPES.EMPLOYEE_INFO_SELECTION]: "社員情報設定",
  [APP_PART_TYPES.CUSTOMER_SELECTION]: "取引先名設定",
  [APP_PART_TYPES.EMPLOYEE_SIGNATURE]: "社員署名設定",
  [APP_PART_TYPES.WORKER_ADDRESSES_SELECTION]: "住所設定",
  [APP_PART_TYPES.WORKER_PHONE_NUMBERS_LANDLINE_SELECTION]: "電話番号設定",
  [APP_PART_TYPES.WORKER_PHONE_NUMBERS_MOBILE_SELECTION]: "携帯番号設定",
  [APP_PART_TYPES.USER_WORKER_EMAILS_SELECTION]: "メールアドレス設定",
  [APP_PART_TYPES.WORKER_STORES_SELECTION]: "屋号設定",
  [APP_PART_TYPES.WORKER_REGISTRATION_NUMBERS_SELECTION]:
    "インボイス登録番号設定",
  [APP_PART_TYPES.WORKER_EMERGENCY_CONTACTS_SELECTION]: "連絡先氏名設定",
  [APP_PART_TYPES.WORKER_EMERGENCY_ADDRESSES_SELECTION]: "連絡先住所設定",
  [APP_PART_TYPES.WORKER_ONBOARDINGS_SELECTION]: "スタッフ番号設定",
  [APP_PART_TYPES.WORKER_INSURANCES_REGULAR_INSURANCE_SELECTION]:
    "社会保険証番号設定",
  [APP_PART_TYPES.WORKER_INSURANCES_UNEMPLOYMENT_INSURANCE_SELECTION]:
    "雇用保険被保険者番号設定",
  [APP_PART_TYPES.WORKER_COMPANY_EMAILS_SELECTION]:
    "会社発行メールアドレス設定",
  [APP_PART_TYPES.WORKER_DEVICES_SELECTION]: "業務端末電話番号設定",
};

export const GROUP_AS_COLORS = {
  typeObjects: {
    bgColor: "#c3c3c3",
    name: "オブジェクト",
    type: "typeObjects",
    color: "#fff",
  },
  typeTables: {
    bgColor: "#22b14c",
    name: "表·参照",
    type: "typeTables",
    color: "#fff",
  },
  typeColumns: {
    bgColor: "#71a5e4",
    name: "列 (フィールド·セル)",
    type: "typeColumns",
    color: "#fff",
  },
};

export const TAGS = [
  {
    name: "ラベル",
    app_part_type: APP_PART_TYPES.LABEL,
    item_config_data: {
      minH: 1,
      h: 2,
      minW: DEFAULT_MIN_W,
      w: 48,
    },
    group_type: GROUP_AS_COLORS.typeObjects.type,
  },
  {
    name: "文字例 (1行)",
    app_part_type: APP_PART_TYPES.TEXT,
    item_config_data: {
      minH: 1,
      h: 3,
      minW: DEFAULT_MIN_W,
      w: 48,
    },
    field_code: "文字例 (1行)",
  },
  {
    name: "文字例（複数行）",
    app_part_type: APP_PART_TYPES.TEXT_AREA,
    item_config_data: {
      h: 3,
      minH: 1,
      minW: DEFAULT_MIN_W,
      w: 48,
    },
    icon: <BsTextarea />,
    field_code: "文字例（複数行）",
  },
  {
    name: "Bizシート参照",
    app_part_type: APP_PART_TYPES.RELATED_RECORDS,
    item_config_data: {
      w: 48,
      minW: DEFAULT_MIN_W,
      h: 3,
      minH: 1,
    },
    icon: <BsNewspaper size={17} />,
    field_code: "Bizシート参照",
  },
  {
    name: "リッチエディター",
    app_part_type: APP_PART_TYPES.RICH_TEXT,
    text: EditorState.createEmpty(),
    item_config_data: {
      w: 80,
      h: 4,
      minH: 1,
      minW: DEFAULT_MIN_W,
    },
    field_code: "リッチエディター",
    icon: <BsJournalRichtext />,
  },
  {
    name: "数値",
    app_part_type: APP_PART_TYPES.NUMBER,
    item_config_data: {
      minH: 1,
      h: 3,
      minW: DEFAULT_MIN_W,
      w: 48,
    },
    icon: <TbNumbers />,
    field_code: "数値",
  },
  {
    name: "計算",
    app_part_type: APP_PART_TYPES.CALCULATED,
    item_config_data: {
      minH: 1,
      h: 3,
      minW: DEFAULT_MIN_W,
      w: 48,
    },
  },
  {
    name: "ラジオボタン",
    app_part_type: APP_PART_TYPES.RADIO_BUTTON,
    options: [{ text: "Option 1" }],
    defaultValue: 0,
    item_config_data: {
      minH: 1,
      h: 3,
      minW: DEFAULT_MIN_W,
      w: 48,
      layout: LAYOUT.HORIZONTAL,
      defaultW: 48,
    },
    field_code: "ラジオボタン",
    icon: <BiRadioCircleMarked size={20} />,
  },
  {
    name: "チェックボックス",
    app_part_type: APP_PART_TYPES.CHECK_BOX,
    item_config_data: {
      minH: 1,
      h: 3,
      minW: DEFAULT_MIN_W,
      w: 48,
    },
    icon: <BiCheckboxChecked size={20} />,
    field_code: "チェックボックス",
  },
  {
    name: "複数選択",
    app_part_type: APP_PART_TYPES.MULTI_CHOICE,
    item_config_data: {
      h: 3,
      minH: 1,
      minW: DEFAULT_MIN_W,
      w: 48,
      options: [
        { text: "サンプル1" },
        { text: "サンプル2" },
        { text: "サンプル3" },
      ],
    },
    icon: <BiSelectMultiple />,
    field_code: "複数選択",
  },
  {
    name: "ドロップダウン",
    app_part_type: APP_PART_TYPES.DROPDOWN,
    item_config_data: {
      minH: 1,
      h: 3,
      minW: DEFAULT_MIN_W,
      w: 48,
    },
    icon: <MdArrowDropDown />,
    field_code: "ドロップダウン",
  },
  {
    name: "日付",
    app_part_type: APP_PART_TYPES.DATE,
    calc_type: "date",
    item_config_data: {
      minW: DEFAULT_MIN_W,
      w: 27,
      minH: 1,
      h: 3,
    },
    icon: <AiOutlineCalendar size={20} />,
    field_code: "日付",
  },
  {
    name: "時刻",
    app_part_type: APP_PART_TYPES.TIME,
    calc_type: "time",
    item_config_data: {
      minW: DEFAULT_MIN_W,
      minH: 1,
      h: 3,
      w: 14,
    },
    icon: <IoMdTime size={20} />,
    field_code: "時刻",
  },
  {
    name: "日付と時刻",
    app_part_type: APP_PART_TYPES.DATE_AND_TIME,
    item_config_data: {
      minW: DEFAULT_MIN_W,
      minH: 1,
      h: 3,
      w: 33,
    },
    field_code: "日付と時刻",
    calc_type: "date_time",
  },
  {
    name: "添付ファイル",
    app_part_type: APP_PART_TYPES.ATTACHMENT,
    item_config_data: {
      minH: 1,
      h: 3,
      minW: DEFAULT_MIN_W,
      w: 48,
    },
    icon: <ImAttachment size={16} />,
    field_code: "添付ファイル",
    thumbnail_size: THUMBNAIL_SIZE_LIST[0].value,
  },
  {
    name: "リンク",
    app_part_type: APP_PART_TYPES.LINK,
    item_config_data: {
      minH: 1,
      h: 3,
      minW: DEFAULT_MIN_W,
      w: 48,
    },
    icon: <AiOutlineLink size={20} />,
    field_code: "リンク",
  },
  // {
  //   name: "組織選択",
  //   app_part_type: APP_PART_TYPES.ORGANIZATION_SELECTION,
  //   item_config_data: {
  //   },
  // },
  {
    name: "横罫線",
    app_part_type: APP_PART_TYPES.HORIZONTAL_BORDER,
    item_config_data: {
      h: 1,
      minW: DEFAULT_MIN_W,
      w: 48,
    },
    icon: <TbMinus />,
    field_code: "横罫線",
    group_type: GROUP_AS_COLORS.typeObjects.type,
  },
  {
    name: "縦罫線",
    app_part_type: APP_PART_TYPES.VERTICAL_BORDER,
    item_config_data: {
      h: 10,
      minW: 5,
      w: 5,
    },
    icon: <TbMinusVertical />,
    field_code: "縦罫線",
    group_type: GROUP_AS_COLORS.typeObjects.type,
  },
  {
    name: "テーブル",
    app_part_type: APP_PART_TYPES.TABLE,
    item_config_data: {
      minW: DEFAULT_MIN_W,
      w: 96,
      minH: 1,
      h: 4,
    },
    icon: <BiTable size={20} />,
    field_code: "テーブル",
    children: [],
    group_type: GROUP_AS_COLORS.typeTables.type,
  },
  {
    name: "フィルードグループ",
    app_part_type: APP_PART_TYPES.FIELD_GROUP,
    item_config_data: {
      minW: DEFAULT_MIN_W,
      w: 48,
      h: 6,
      minH: 1,
    },
    icon: <AiOutlineGroup size={20} />,
    children: [],
    field_code: "フィルードグループ",
    group_type: GROUP_AS_COLORS.typeTables.type,
  },
  {
    name: "作成者",
    app_part_type: APP_PART_TYPES.CREATED_BY,
    is_required_field: true,
    item_config_data: {
      w: 48,
      minW: DEFAULT_MIN_W,
      h: 3.0,
      minH: 1.0,
    },
    icon: <FaUserAlt size={17} />,
    field_code: "作成者",
  },
  {
    name: "更新者",
    app_part_type: APP_PART_TYPES.UPDATED_BY,
    is_required_field: true,
    item_config_data: {
      w: 48,
      minW: DEFAULT_MIN_W,
      h: 3,
      minH: 1,
    },
    icon: <FaUserAlt size={17} />,
    field_code: "更新者",
  },
  {
    name: "作成日時",
    app_part_type: APP_PART_TYPES.CREATED_DATETIME,
    is_required_field: true,
    item_config_data: {
      w: 48,
      minW: DEFAULT_MIN_W,
      h: 3,
      minH: 1,
    },
    icon: <AiOutlineCalendar size={17} />,
    field_code: "作成日時",
  },
  {
    name: "更新日時",
    app_part_type: APP_PART_TYPES.UPDATED_DATETIME,
    is_required_field: true,
    item_config_data: {
      w: 48,
      minW: DEFAULT_MIN_W,
      h: 3,
      minH: 1,
    },
    icon: <AiOutlineCalendar size={17} />,
    field_code: "更新日時",
  },
  {
    name: "レコード数",
    app_part_type: APP_PART_TYPES.RECORD_NUMBER,
    is_required_field: true,
    item_config_data: {
      w: 48,
      minW: DEFAULT_MIN_W,
      h: 3,
      minH: 1,
    },
    icon: <TbNumbers size={17} />,
    field_code: "レコード数",
  },
  {
    name: "検索",
    app_part_type: APP_PART_TYPES.LOOKUP,
    item_config_data: {
      w: 48,
      minW: DEFAULT_MIN_W,
      h: 3,
      minH: 1,
    },
    icon: <RiFileSearchLine size={17} />,
    field_code: "検索",
  },
  {
    name: "組織選択",
    app_part_type: APP_PART_TYPES.ORGANIZATION_SELECTION,
    item_config_data: {
      w: 48,
      minW: DEFAULT_MIN_W,
      h: 3,
      minH: 1,
    },
    icon: <RiOrganizationChart size={18} />,
    field_code: "組織選択",
  },
  {
    name: "部署選択",
    app_part_type: APP_PART_TYPES.DIVISION_SELECTION,
    item_config_data: {
      w: 48,
      minW: DEFAULT_MIN_W,
      h: 3,
      minH: 1,
    },
    icon: <BsFillDiagram2Fill size={18} />,
    field_code: "部署選択",
  },
  {
    name: "部門選択",
    app_part_type: APP_PART_TYPES.DEPARTMENT_SELECTION,
    item_config_data: {
      w: 48,
      minW: DEFAULT_MIN_W,
      h: 3,
      minH: 1,
    },
    icon: <VscOrganization size={18} />,
    field_code: "部門選択",
  },
  {
    name: "支店選択",
    app_part_type: APP_PART_TYPES.BRANCH_SELECTION,
    item_config_data: {
      w: 48,
      minW: DEFAULT_MIN_W,
      h: 3,
      minH: 1,
    },
    icon: <AiOutlineBranches size={18} />,
    field_code: "支店選択",
  },
  {
    name: "社員選択",
    app_part_type: APP_PART_TYPES.WORKER_SELECTION,
    item_config_data: {
      w: 48,
      h: 3,
      minW: DEFAULT_MIN_W,
      minH: 1,
    },
    icon: <AiOutlineUser size={18} />,
    field_code: "社員選択",
  },
  {
    name: "自社名",
    app_part_type: APP_PART_TYPES.PRIMARY_COMPANY_NAME,
    item_config_data: {
      w: 48,
      minW: DEFAULT_MIN_W,
      h: 3,
      minH: 1,
    },
    icon: <AiOutlineBank size={17} />,
    field_code: "自社名",
    group_type: GROUP_AS_COLORS.typeColumns.type,
  },
  {
    name: "自社住所",
    app_part_type: APP_PART_TYPES.COMPANY_LOCATION_SELECTION,
    item_config_data: {
      w: 48,
      h: 3,
      minW: DEFAULT_MIN_W,
      minH: 1,
    },
    icon: <AiOutlineBranches size={18} />,
    field_code: "自社住所",
  },
  {
    name: "代表者名",
    app_part_type: APP_PART_TYPES.REPRESENTATIVE_SELECTION,
    item_config_data: {
      w: 48,
      h: 3,
      minW: DEFAULT_MIN_W,
      minH: 1,
    },
    icon: <FaUserAlt size={18} />,
    field_code: "代表者名",
  },
  {
    name: "会社電話番号",
    app_part_type: APP_PART_TYPES.COMPANY_PHONE_NUMBER_SELECTION,
    item_config_data: {
      w: 48,
      minW: DEFAULT_MIN_W,
      h: 3,
      minH: 1,
    },
    icon: <AiOutlinePhone size={18} />,
    field_code: "会社電話番号",
    group_type: GROUP_AS_COLORS.typeColumns.type,
  },
  {
    name: "役職",
    app_part_type: APP_PART_TYPES.POSITION_SELECTION,
    item_config_data: {
      w: 48,
      minW: DEFAULT_MIN_W,
      h: 3,
      minH: 1,
    },
    icon: <MdArrowDropDown size={18} />,
    field_code: "役職",
    group_type: GROUP_AS_COLORS.typeColumns.type,
  },
  {
    name: "社員情報",
    app_part_type: APP_PART_TYPES.EMPLOYEE_INFO_SELECTION,
    item_config_data: {
      w: 48,
      minW: DEFAULT_MIN_W,
      h: 3,
      minH: 1,
      employee_info_color: "#174a83",
    },
    icon: <AiOutlineUser size={18} />,
    field_code: "社員情報",
    group_type: GROUP_AS_COLORS.typeColumns.type,
  },
  {
    name: "取引先名",
    app_part_type: APP_PART_TYPES.CUSTOMER_SELECTION,
    item_config_data: {
      w: 48,
      minW: DEFAULT_MIN_W,
      h: 3,
      minH: 1,
    },
    icon: <BsNewspaper size={17} />,
    field_code: "取引先名",
    group_type: GROUP_AS_COLORS.typeColumns.type,
  },
  {
    name: "署名",
    app_part_type: APP_PART_TYPES.EMPLOYEE_SIGNATURE,
    item_config_data: {
      w: 48,
      minW: DEFAULT_MIN_W,
      h: 3,
      minH: 1,
    },
    icon: <FaSignature size={17} />,
    field_code: "署名",
    group_type: GROUP_AS_COLORS.typeColumns.type,
  },
] as AppPartType[];

export const ITEM_SIZE_RELY_ON_CONTENT_TYPES = [
  "check_box",
  "radio_button",
  "branch_selection",
  "organization_selection",
  "division_selection",
  "organization_selection",
] as AppPartType["app_part_type"][];

export const OBJECT_TYPE_PERMISSION = {
  WORKER: "Worker",
  USER: "User",
  DEPARTMENT: "Department",
  ORGANIZATION: "Organization",
  POSITION: "Position",
};

export const TARGET_OBJECT_TYPE_PERMISSION = {
  APP: "App",
  APP_RECORD: "AppRecord",
  APP_RECORD_VALUE: "AppRecordValue",
  APP_PART: "AppPart",
};

export const UNIT_OF_MEASURE_TYPE = {
  frefix: "frefix",
  suffix: "suffix",
  frefix_suffix: "frefix_suffix",
};

export const ERROR_WHEN_SAVING_MSG = "保存に失敗しました。";
export const EMPTY_TABLE_MSG =
  "テーブルにフィールドが含まれていないテーブルを保存することはできません。テーブルに１つ以上のフィールドを追加するか、テーブルを削除してください。";
export const WARNING_DELETE_MESSAGE =
  "この列を削除すると、列に入力されたデータも削除されます。列を削除しましか？";
export enum CustomAppSelectOption {
  WORKER = "worker",
  POSITION = "position",
  ORG = "org",
}

export const DEFAULT_WIDTH_COL = 200;

export const AUTO_TYPES = {
  [APP_PART_TYPES.RECORD_NUMBER]: APP_PART_TYPES.RECORD_NUMBER,
  [APP_PART_TYPES.UPDATED_BY]: APP_PART_TYPES.UPDATED_BY,
  [APP_PART_TYPES.CREATED_BY]: APP_PART_TYPES.CREATED_BY,
  [APP_PART_TYPES.CREATED_DATETIME]: APP_PART_TYPES.CREATED_DATETIME,
  [APP_PART_TYPES.UPDATED_DATETIME]: APP_PART_TYPES.UPDATED_DATETIME,
  [APP_PART_TYPES.CALCULATED]: APP_PART_TYPES.CALCULATED,
  [APP_PART_TYPES.PRIMARY_COMPANY_NAME]: APP_PART_TYPES.PRIMARY_COMPANY_NAME,
  [APP_PART_TYPES.EMPLOYEE_SIGNATURE]: APP_PART_TYPES.EMPLOYEE_SIGNATURE,
};

export const SEPARATOR = ",";

export const CUSTOM_OPTION = [
  {
    label: "社員",
    value: CustomAppSelectOption.WORKER,
  },
  {
    label: "役職",
    value: CustomAppSelectOption.POSITION,
  },
  {
    label: "組織",
    value: CustomAppSelectOption.ORG,
  },
];

export const EMPLOYEE_INFO_CHILDREN_SELECTION = [
  {
    name: "住所",
    app_part_type: APP_PART_TYPES.WORKER_ADDRESSES_SELECTION,
    item_config_data: {
      w: 48,
      minW: DEFAULT_MIN_W,
      h: 3,
      minH: 1,
    },
    icon: <AiOutlineUser size={18} />,
    field_code: "住所",
    group_type: GROUP_AS_COLORS.typeColumns.type,
  },
  {
    name: "電話番号",
    app_part_type: APP_PART_TYPES.WORKER_PHONE_NUMBERS_LANDLINE_SELECTION,
    item_config_data: {
      w: 48,
      minW: DEFAULT_MIN_W,
      h: 3,
      minH: 1,
    },
    icon: <AiOutlineUser size={18} />,
    field_code: "電話番号",
    group_type: GROUP_AS_COLORS.typeColumns.type,
  },
  {
    name: "携帯番号",
    app_part_type: APP_PART_TYPES.WORKER_PHONE_NUMBERS_MOBILE_SELECTION,
    item_config_data: {
      w: 48,
      minW: DEFAULT_MIN_W,
      h: 3,
      minH: 1,
    },
    icon: <AiOutlineUser size={18} />,
    field_code: "携帯番号",
    group_type: GROUP_AS_COLORS.typeColumns.type,
  },
  {
    name: "メールアドレス",
    app_part_type: APP_PART_TYPES.USER_WORKER_EMAILS_SELECTION,
    item_config_data: {
      w: 48,
      minW: DEFAULT_MIN_W,
      h: 3,
      minH: 1,
    },
    icon: <AiOutlineUser size={18} />,
    field_code: "メールアドレス",
    group_type: GROUP_AS_COLORS.typeColumns.type,
  },
  {
    name: "屋号",
    app_part_type: APP_PART_TYPES.WORKER_STORES_SELECTION,
    item_config_data: {
      w: 48,
      minW: DEFAULT_MIN_W,
      h: 3,
      minH: 1,
    },
    icon: <AiOutlineUser size={18} />,
    field_code: "屋号",
    group_type: GROUP_AS_COLORS.typeColumns.type,
  },
  {
    name: "インボイス登録番号",
    app_part_type: APP_PART_TYPES.WORKER_REGISTRATION_NUMBERS_SELECTION,
    item_config_data: {
      w: 48,
      minW: DEFAULT_MIN_W,
      h: 3,
      minH: 1,
    },
    icon: <AiOutlineUser size={18} />,
    field_code: "インボイス登録番号",
    group_type: GROUP_AS_COLORS.typeColumns.type,
  },
  {
    name: "連絡先氏名",
    app_part_type: APP_PART_TYPES.WORKER_EMERGENCY_CONTACTS_SELECTION,
    item_config_data: {
      w: 48,
      minW: DEFAULT_MIN_W,
      h: 3,
      minH: 1,
    },
    icon: <AiOutlineUser size={18} />,
    field_code: "連絡先氏名",
    group_type: GROUP_AS_COLORS.typeColumns.type,
  },
  {
    name: "連絡先住所",
    app_part_type: APP_PART_TYPES.WORKER_EMERGENCY_ADDRESSES_SELECTION,
    item_config_data: {
      w: 48,
      minW: DEFAULT_MIN_W,
      h: 3,
      minH: 1,
    },
    icon: <AiOutlineUser size={18} />,
    field_code: "連絡先住所",
    group_type: GROUP_AS_COLORS.typeColumns.type,
  },
  {
    name: "スタッフ番号",
    app_part_type: APP_PART_TYPES.WORKER_ONBOARDINGS_SELECTION,
    item_config_data: {
      w: 48,
      minW: DEFAULT_MIN_W,
      h: 3,
      minH: 1,
    },
    icon: <AiOutlineUser size={18} />,
    field_code: "スタッフ番号",
    group_type: GROUP_AS_COLORS.typeColumns.type,
  },
  {
    name: "社会保険証番号",
    app_part_type: APP_PART_TYPES.WORKER_INSURANCES_REGULAR_INSURANCE_SELECTION,
    item_config_data: {
      w: 48,
      minW: DEFAULT_MIN_W,
      h: 3,
      minH: 1,
    },
    icon: <AiOutlineUser size={18} />,
    field_code: "社会保険証番号",
    group_type: GROUP_AS_COLORS.typeColumns.type,
  },
  {
    name: "雇用保険被保険者番号",
    app_part_type:
      APP_PART_TYPES.WORKER_INSURANCES_UNEMPLOYMENT_INSURANCE_SELECTION,
    item_config_data: {
      w: 48,
      minW: DEFAULT_MIN_W,
      h: 3,
      minH: 1,
    },
    icon: <AiOutlineUser size={18} />,
    field_code: "雇用保険被保険者番号",
    group_type: GROUP_AS_COLORS.typeColumns.type,
  },
  {
    name: "会社発行メールアドレス",
    app_part_type: APP_PART_TYPES.WORKER_COMPANY_EMAILS_SELECTION,
    item_config_data: {
      w: 48,
      minW: DEFAULT_MIN_W,
      h: 3,
      minH: 1,
    },
    icon: <AiOutlineUser size={18} />,
    field_code: "会社発行メールアドレス",
    group_type: GROUP_AS_COLORS.typeColumns.type,
  },
  {
    name: "業務端末電話番号",
    app_part_type: APP_PART_TYPES.WORKER_DEVICES_SELECTION,
    item_config_data: {
      w: 48,
      minW: DEFAULT_MIN_W,
      h: 3,
      minH: 1,
    },
    icon: <AiOutlineUser size={18} />,
    field_code: "業務端末電話番号",
    group_type: GROUP_AS_COLORS.typeColumns.type,
  },
] as AppPartType[];
